import { useState, useCallback, useRef, useEffect } from 'react';
import type { AudioInputSettings } from '@/types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

/**
 * Connection status representing the current state of audio capture
 */
type ConnectionStatus =
  | 'idle'           // Not started
  | 'initializing'   // Setting up media devices
  | 'streaming'      // Actively capturing and uploading
  | 'paused'         // Temporarily paused
  | 'reconnecting'   // Attempting to recover from errors
  | 'stopped'        // Cleanly stopped
  | 'error';         // Unrecoverable error

/**
 * Audio format supported by the backend
 */
type AudioFormat = 'pcm' | 'opus' | 'webm' | 'ogg';

/**
 * Language code - now supports all Azure Speech SDK languages dynamically
 * Common codes: ko, en, zh, ja, es, fr, de, ar, hi, ru, etc.
 */
type LanguageCode = string;

/**
 * Error classification for retry strategy
 */
interface AudioCaptureError {
  type: 'network' | 'auth' | 'validation' | 'device' | 'server' | 'unknown';
  message: string;
  retryable: boolean;
  statusCode?: number;
}

/**
 * Performance metrics for monitoring upload health
 */
interface UploadMetrics {
  chunksUploaded: number;
  chunksFailed: number;
  averageLatency: number;
  currentLatency: number;
  lastUploadAt: number | null;
}

/**
 * Complete state of the audio capture system
 */
interface AudioCaptureState {
  status: ConnectionStatus;
  isCapturing: boolean;
  error: AudioCaptureError | null;
  audioLevel: number;
  devices: MediaDeviceInfo[];
  metrics: UploadMetrics;
}

/**
 * Chunk upload queue item with retry tracking
 */
interface ChunkQueueItem {
  blob: Blob;
  sequence: number;
  retryCount: number;
  enqueuedAt: number;
}

/**
 * Configuration for exponential backoff retry strategy
 */
const RETRY_CONFIG = {
  maxRetries: 5,
  initialDelayMs: 1000,
  maxDelayMs: 30000,
  backoffMultiplier: 2,
} as const;

/**
 * Upload configuration
 */
const UPLOAD_CONFIG = {
  maxConcurrentUploads: 3,
  chunkTimeSliceMs: 2000,  // 2 seconds per chunk for Azure Speech API
  uploadTimeoutMs: 15000,  // Increased timeout for larger chunks
} as const;

/**
 * Custom hook for HTTP-based audio capture with chunked upload to backend
 *
 * Implements a production-ready audio streaming system with:
 * - MediaRecorder-based audio capture with configurable settings
 * - HTTP POST chunked upload to /room/{roomId}/audio endpoint
 * - JWT authentication with automatic token refresh
 * - Exponential backoff retry strategy for failed uploads
 * - Concurrent upload queue management
 * - Real-time audio level visualization
 * - Comprehensive error handling and recovery
 * - Performance metrics tracking
 * - Custom phrase list support for improved speech recognition
 *
 * @param roomId - The room identifier (1-6) for audio streaming
 * @param sourceLanguage - Source language code for the audio stream
 * @param phrases - Optional custom phrases for speech recognition accuracy
 * @returns Audio capture state and control functions
 */
export function useAudioCapture(
  roomId: string,
  sourceLanguage: LanguageCode = 'ko',
  phrases: string[] = []
) {
  const [state, setState] = useState<AudioCaptureState>({
    status: 'idle',
    isCapturing: false,
    error: null,
    audioLevel: 0,
    devices: [],
    metrics: {
      chunksUploaded: 0,
      chunksFailed: 0,
      averageLatency: 0,
      currentLatency: 0,
      lastUploadAt: null,
    },
  });

  // Refs for persistent state across renders
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const chunkSequenceRef = useRef<number>(0);
  const uploadQueueRef = useRef<ChunkQueueItem[]>([]);
  const activeUploadsRef = useRef<number>(0);
  const latencyBufferRef = useRef<number[]>([]);
  const isStreamingRef = useRef<boolean>(false);

  /**
   * Load available audio input devices
   */
  const loadAudioDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioDevices = devices.filter((device) => device.kind === 'audioinput');
      setState((prev) => ({ ...prev, devices: audioDevices }));
    } catch (error) {
      console.error('Failed to enumerate devices:', error);
    }
  }, []);

  /**
   * Cleanup function for stopping all audio capture resources
   */
  const cleanup = useCallback(() => {
    // Stop animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Stop media recorder or script processor
    if (mediaRecorderRef.current) {
      // Check if it's a MediaRecorder
      if ('stop' in mediaRecorderRef.current && (mediaRecorderRef.current as MediaRecorder).state !== 'inactive') {
        (mediaRecorderRef.current as MediaRecorder).stop();
      }
      // Check if it's a ScriptProcessorNode
      else if ('disconnect' in mediaRecorderRef.current) {
        (mediaRecorderRef.current as unknown as ScriptProcessorNode).disconnect();
      }
      mediaRecorderRef.current = null;
    }

    // Stop media stream
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    analyserRef.current = null;
    uploadQueueRef.current = [];
    activeUploadsRef.current = 0;
    chunkSequenceRef.current = 0;
    latencyBufferRef.current = [];
    isStreamingRef.current = false;
  }, []);

  /**
   * Classify error for retry strategy decision
   */
  const classifyError = useCallback((error: any, statusCode?: number): AudioCaptureError => {
    if (statusCode === 401) {
      return {
        type: 'auth',
        message: 'Authentication failed',
        retryable: true,
        statusCode,
      };
    }

    if (statusCode === 400 || statusCode === 403 || statusCode === 404) {
      return {
        type: 'validation',
        message: `Invalid request: ${error.message || 'Validation error'}`,
        retryable: false,
        statusCode,
      };
    }

    if (statusCode && statusCode >= 500) {
      return {
        type: 'server',
        message: 'Server error',
        retryable: true,
        statusCode,
      };
    }

    if (error.name === 'TypeError' || error.message?.includes('fetch') || error.message?.includes('network')) {
      return {
        type: 'network',
        message: 'Network connection error',
        retryable: true,
      };
    }

    if (error.name === 'NotAllowedError' || error.name === 'NotFoundError') {
      return {
        type: 'device',
        message: error.message || 'Microphone access error',
        retryable: false,
      };
    }

    return {
      type: 'unknown',
      message: error.message || 'Unknown error occurred',
      retryable: true,
    };
  }, []);

  /**
   * Calculate exponential backoff delay for retry attempts
   */
  const calculateBackoffDelay = useCallback((retryCount: number): number => {
    const delay = RETRY_CONFIG.initialDelayMs * Math.pow(RETRY_CONFIG.backoffMultiplier, retryCount);
    return Math.min(delay, RETRY_CONFIG.maxDelayMs);
  }, []);

  /**
   * Update performance metrics with new upload latency
   */
  const updateMetrics = useCallback((latency: number, success: boolean) => {
    setState((prev) => {
      latencyBufferRef.current.push(latency);
      if (latencyBufferRef.current.length > 10) {
        latencyBufferRef.current.shift();
      }

      const averageLatency = latencyBufferRef.current.reduce((a, b) => a + b, 0) / latencyBufferRef.current.length;

      return {
        ...prev,
        metrics: {
          chunksUploaded: success ? prev.metrics.chunksUploaded + 1 : prev.metrics.chunksUploaded,
          chunksFailed: success ? prev.metrics.chunksFailed : prev.metrics.chunksFailed + 1,
          averageLatency: Math.round(averageLatency),
          currentLatency: Math.round(latency),
          lastUploadAt: Date.now(),
        },
      };
    });
  }, []);

  /**
   * Upload a single audio chunk to the backend with retry logic
   */
  const uploadChunk = useCallback(async (
    chunk: Blob,
    sequence: number,
    retryCount: number = 0
  ): Promise<void> => {
    const uploadStartTime = Date.now();
    console.log('[AudioCapture] Starting upload for chunk #', sequence, 'size:', chunk.size, 'bytes, attempt:', retryCount + 1);

    try {
      // Get token if available (optional for public streaming)
      const token = localStorage.getItem('accessToken');
      console.log('[AudioCapture] Token available:', !!token);

      // Validate room ID format
      const roomIdNum = parseInt(roomId, 10);
      if (isNaN(roomIdNum) || roomIdNum < 1 || roomIdNum > 6) {
        throw new Error('Invalid room ID');
      }

      // Determine audio format from blob type
      let audioFormat: AudioFormat = 'pcm'; // default to PCM (raw audio)
      if (chunk.type.includes('pcm')) {
        audioFormat = 'pcm';
      } else if (chunk.type.includes('ogg')) {
        audioFormat = 'ogg';
      } else if (chunk.type.includes('webm')) {
        audioFormat = 'webm';
      } else if (chunk.type.includes('opus')) {
        audioFormat = 'ogg'; // Fallback: if only 'opus' is detected, assume ogg container
      }

      // Convert blob to ArrayBuffer
      const arrayBuffer = await chunk.arrayBuffer();

      // Upload chunk with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), UPLOAD_CONFIG.uploadTimeoutMs);

      // Build headers - Authorization is optional
      const headers: Record<string, string> = {
        'Content-Type': 'application/octet-stream',
        'X-Chunk-Sequence': sequence.toString(),
        'X-Source-Language': sourceLanguage,
        'X-Audio-Format': audioFormat,
        'X-Sample-Rate': audioContextRef.current?.sampleRate.toString() || '48000',
      };

      // Add custom phrases for speech recognition (only on first chunk)
      if (sequence === 0 && phrases.length > 0) {
        headers['X-Custom-Phrases'] = JSON.stringify(phrases);
      }

      // Only add Authorization header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/room/${roomIdNum}/audio`, {
        method: 'POST',
        headers,
        body: arrayBuffer,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Handle non-ok responses
      if (!response.ok) {
        console.error('[AudioCapture] Upload failed with status:', response.status, response.statusText);
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.error || `Upload failed with status ${response.status}`);
        throw Object.assign(error, { statusCode: response.status });
      }

      // Success - update metrics
      const latency = Date.now() - uploadStartTime;
      console.log('[AudioCapture] Upload successful for chunk #', sequence, 'latency:', latency, 'ms');
      updateMetrics(latency, true);

      activeUploadsRef.current--;
      processUploadQueue();
    } catch (error: any) {
      const latency = Date.now() - uploadStartTime;
      updateMetrics(latency, false);

      // Classify error and decide on retry
      const classified = classifyError(error, error.statusCode);

      // Handle authentication errors with token refresh
      if (classified.type === 'auth' && retryCount === 0) {
        try {
          const refreshToken = localStorage.getItem('refreshToken');
          if (refreshToken) {
            const response = await fetch(`${API_URL}/api/auth/refresh`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ refreshToken }),
            });

            if (response.ok) {
              const { accessToken, refreshToken: newRefreshToken } = await response.json();
              localStorage.setItem('accessToken', accessToken);
              localStorage.setItem('refreshToken', newRefreshToken);

              // Retry upload with new token
              return uploadChunk(chunk, sequence, retryCount + 1);
            }
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
        }
      }

      // Retry if error is retryable and under retry limit
      if (classified.retryable && retryCount < RETRY_CONFIG.maxRetries) {
        const delay = calculateBackoffDelay(retryCount);
        console.warn(`Upload failed (attempt ${retryCount + 1}), retrying in ${delay}ms:`, classified.message);

        activeUploadsRef.current--;

        await new Promise(resolve => setTimeout(resolve, delay));
        return uploadChunk(chunk, sequence, retryCount + 1);
      }

      // Non-retryable or max retries exceeded
      activeUploadsRef.current--;
      console.error('Upload failed permanently:', classified);

      setState((prev) => ({
        ...prev,
        status: 'error',
        error: classified,
      }));

      throw error;
    }
  }, [roomId, sourceLanguage, classifyError, calculateBackoffDelay, updateMetrics]);

  /**
   * Process upload queue with concurrent upload limit
   */
  const processUploadQueue = useCallback(() => {
    while (
      uploadQueueRef.current.length > 0 &&
      activeUploadsRef.current < UPLOAD_CONFIG.maxConcurrentUploads
    ) {
      const item = uploadQueueRef.current.shift();
      if (item) {
        activeUploadsRef.current++;
        uploadChunk(item.blob, item.sequence, item.retryCount).catch((error) => {
          console.error('Upload queue processing error:', error);
        });
      }
    }
  }, [uploadChunk]);

  /**
   * Enqueue audio chunk for upload
   */
  const enqueueChunk = useCallback((blob: Blob, sequence: number) => {
    console.log('[AudioCapture] Adding chunk to upload queue - sequence:', sequence, 'size:', blob.size, 'bytes, queue length before:', uploadQueueRef.current.length);
    uploadQueueRef.current.push({
      blob,
      sequence,
      retryCount: 0,
      enqueuedAt: Date.now(),
    });
    console.log('[AudioCapture] Queue length after:', uploadQueueRef.current.length, 'active uploads:', activeUploadsRef.current);

    processUploadQueue();
  }, [processUploadQueue]);

  /**
   * Monitor audio level for visualization
   */
  const monitorAudioLevel = useCallback(() => {
    if (!analyserRef.current) return;

    const analyser = analyserRef.current;
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    let logCounter = 0;
    let smoothedLevel = 0; // Smoothed audio level for gradual changes

    const updateLevel = () => {
      analyser.getByteFrequencyData(dataArray);

      // Calculate average level
      const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
      const normalizedLevel = Math.min(100, (average / 255) * 100);

      // Apply exponential smoothing to prevent rapid fluctuations
      // Higher alpha = faster response, lower = smoother (0.5 = balanced)
      const alpha = 0.5;
      smoothedLevel = alpha * normalizedLevel + (1 - alpha) * smoothedLevel;

      // Log audio level every 60 frames (about once per second at 60fps)
      logCounter++;
      if (logCounter >= 60) {
        // Also log first few values of dataArray to see if we're getting real data
        const sampleValues = Array.from(dataArray.slice(0, 10));
        console.log('[AudioCapture] Audio level:', smoothedLevel.toFixed(1), '% (raw:', normalizedLevel.toFixed(1), '%, raw average:', average.toFixed(1), ')');
        console.log('[AudioCapture] First 10 frequency values:', sampleValues);

        // Check if stream is still active
        if (mediaStreamRef.current) {
          const tracks = mediaStreamRef.current.getTracks();
          const audioTrack = tracks.find(t => t.kind === 'audio');
          if (audioTrack) {
            console.log('[AudioCapture] Audio track state:', {
              enabled: audioTrack.enabled,
              muted: audioTrack.muted,
              readyState: audioTrack.readyState
            });
          }
        }

        // Check AudioContext state
        if (audioContextRef.current) {
          console.log('[AudioCapture] AudioContext state:', audioContextRef.current.state);
        }

        logCounter = 0;
      }

      setState((prev) => ({ ...prev, audioLevel: smoothedLevel }));

      animationFrameRef.current = requestAnimationFrame(updateLevel);
    };

    updateLevel();
  }, []);

  /**
   * Start audio capture with specified settings
   */
  const startCapture = useCallback(async (settings: AudioInputSettings) => {
    try {
      console.log('[AudioCapture] Starting capture with settings:', settings);
      setState((prev) => ({ ...prev, status: 'initializing', error: null }));

      // Authentication check removed - streaming works without login
      console.log('[AudioCapture] Proceeding without authentication check');

      // Get user media with constraints
      const constraints: MediaStreamConstraints = {
        audio: {
          // Use 'ideal' instead of 'exact' to allow fallback to default device
          deviceId: settings.deviceId ? { ideal: settings.deviceId } : undefined,
          echoCancellation: settings.echoCancellation,
          noiseSuppression: settings.noiseSuppression,
          autoGainControl: settings.autoGainControl,
          sampleRate: settings.sampleRate,
          channelCount: settings.channelCount,
        },
      };

      console.log('[AudioCapture] Requesting microphone access with constraints:', constraints);

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (deviceError) {
        // If device fails (either specific or ideal), retry with minimal constraints
        if ((deviceError as Error).name === 'NotFoundError' || (deviceError as Error).name === 'NotReadableError') {
          console.warn('[AudioCapture] Device access failed, retrying with minimal constraints');
          const fallbackConstraints: MediaStreamConstraints = {
            audio: true,  // Use browser default with no specific constraints
          };
          stream = await navigator.mediaDevices.getUserMedia(fallbackConstraints);
        } else {
          throw deviceError;
        }
      }
      console.log('[AudioCapture] Microphone access granted, stream:', stream);

      // Detailed stream inspection
      const tracks = stream.getTracks();
      console.log('[AudioCapture] Stream tracks:', tracks);
      tracks.forEach((track, index) => {
        console.log(`[AudioCapture] Track ${index}:`, {
          kind: track.kind,
          label: track.label,
          enabled: track.enabled,
          muted: track.muted,
          readyState: track.readyState,
          settings: track.getSettings()
        });
      });

      mediaStreamRef.current = stream;

      // Setup audio context for visualization
      console.log('[AudioCapture] Creating audio context...');
      const audioContext = new AudioContext({ sampleRate: settings.sampleRate });
      audioContextRef.current = audioContext;

      console.log('[AudioCapture] AudioContext state:', audioContext.state);
      console.log('[AudioCapture] AudioContext sampleRate:', audioContext.sampleRate);

      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;  // Reduced from 2048 for better real-time responsiveness
      analyser.minDecibels = -90;
      analyser.maxDecibels = -10;
      analyser.smoothingTimeConstant = 0.3;  // Reduced from 0.8 for more responsive visualization

      // Create a silent gain node to ensure audio graph is actively processed
      // Without a destination, browser may skip processing the audio graph
      const gainNode = audioContext.createGain();
      gainNode.gain.value = 0;  // Silent - no playback, just for graph processing

      // IMPORTANT: For Bluetooth devices, connect source to BOTH analyser AND gain node
      // to ensure audio processing happens even if analyser alone doesn't trigger processing
      source.connect(analyser);
      source.connect(gainNode);  // Direct connection from source for Bluetooth compatibility
      gainNode.connect(audioContext.destination);

      analyserRef.current = analyser;
      console.log('[AudioCapture] Audio context and analyser created');
      console.log('[AudioCapture] MediaStreamSource → AnalyserNode (parallel)');
      console.log('[AudioCapture] MediaStreamSource → GainNode → Destination (silent)');

      // Start audio level monitoring
      console.log('[AudioCapture] Starting audio level monitoring...');
      monitorAudioLevel();

      // Setup ScriptProcessorNode for raw PCM audio capture
      // This approach sends raw PCM data directly to Azure Speech SDK
      // avoiding WebM container format issues with streaming
      console.log('[AudioCapture] Setting up ScriptProcessorNode for raw PCM capture...');

      const bufferSize = 4096; // Must be power of 2: 256, 512, 1024, 2048, 4096, 8192, 16384
      const inputChannels = 1; // Mono
      const outputChannels = 1; // Mono

      const scriptProcessor = audioContext.createScriptProcessor(bufferSize, inputChannels, outputChannels);

      // Buffer to accumulate audio samples before sending
      let pcmBuffer: Float32Array[] = [];
      let pcmBufferSize = 0;
      const targetChunkSize = Math.floor((UPLOAD_CONFIG.chunkTimeSliceMs / 1000) * audioContext.sampleRate);

      console.log('[AudioCapture] ScriptProcessorNode config:', {
        bufferSize,
        sampleRate: audioContext.sampleRate,
        targetChunkSize,
        targetChunkMs: UPLOAD_CONFIG.chunkTimeSliceMs
      });

      scriptProcessor.onaudioprocess = (event) => {
        if (!isStreamingRef.current) return;

        const inputData = event.inputBuffer.getChannelData(0); // Get mono channel

        // Accumulate samples
        pcmBuffer.push(new Float32Array(inputData));
        pcmBufferSize += inputData.length;

        // Send chunk when we have enough samples
        if (pcmBufferSize >= targetChunkSize) {
          // Concatenate all buffers
          const combinedBuffer = new Float32Array(pcmBufferSize);
          let offset = 0;
          for (const buffer of pcmBuffer) {
            combinedBuffer.set(buffer, offset);
            offset += buffer.length;
          }

          // Convert Float32Array to Int16Array (PCM 16-bit)
          const int16Buffer = new Int16Array(combinedBuffer.length);
          for (let i = 0; i < combinedBuffer.length; i++) {
            const s = Math.max(-1, Math.min(1, combinedBuffer[i]));
            int16Buffer[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
          }

          // Create Blob with raw PCM data
          const pcmBlob = new Blob([int16Buffer.buffer], { type: 'audio/pcm' });

          const sequence = chunkSequenceRef.current++;
          console.log('[AudioCapture] Sending PCM chunk #', sequence, 'samples:', int16Buffer.length, 'bytes:', pcmBlob.size);
          enqueueChunk(pcmBlob, sequence);

          // Reset buffer
          pcmBuffer = [];
          pcmBufferSize = 0;
        }
      };

      // Connect ScriptProcessorNode to audio graph
      source.connect(scriptProcessor);
      scriptProcessor.connect(audioContext.destination); // Must connect to destination for processing to occur

      mediaRecorderRef.current = scriptProcessor as any; // Store for cleanup

      // Reset sequence counter and enable streaming
      chunkSequenceRef.current = 0;
      isStreamingRef.current = true;

      console.log('[AudioCapture] ScriptProcessorNode started, streaming raw PCM');

      console.log('[AudioCapture] Capture started successfully');
      setState((prev) => ({
        ...prev,
        status: 'streaming',
        isCapturing: true,
        error: null,
      }));
    } catch (error: any) {
      console.error('[AudioCapture] Error during startCapture:', error);
      console.error('[AudioCapture] Error name:', error.name);
      console.error('[AudioCapture] Error message:', error.message);

      const classified = classifyError(error);
      console.error('[AudioCapture] Classified error:', classified);

      setState((prev) => ({
        ...prev,
        status: 'error',
        isCapturing: false,
        error: classified,
      }));

      cleanup();
      throw error;
    }
  }, [monitorAudioLevel, enqueueChunk, classifyError, cleanup, state.status]);

  /**
   * Stop audio capture and cleanup resources
   */
  const stopCapture = useCallback(() => {
    cleanup();

    setState((prev) => ({
      ...prev,
      status: 'stopped',
      isCapturing: false,
      audioLevel: 0,
    }));
  }, [cleanup]);

  /**
   * Pause audio capture temporarily
   */
  const pauseCapture = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      isStreamingRef.current = false;

      setState((prev) => ({
        ...prev,
        status: 'paused',
        isCapturing: false,
      }));
    }
  }, []);

  /**
   * Resume paused audio capture
   */
  const resumeCapture = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      isStreamingRef.current = true;

      setState((prev) => ({
        ...prev,
        status: 'streaming',
        isCapturing: true,
      }));
    }
  }, []);

  // Load devices on mount
  useEffect(() => {
    loadAudioDevices();
  }, [loadAudioDevices]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    ...state,
    startCapture,
    stopCapture,
    pauseCapture,
    resumeCapture,
    loadAudioDevices,
  };
}
